import { ClusterSettings } from './cluster';
import type * as Shared from './shared';

export namespace AdminSection {
  export enum ClusterDetailsTab {
    Nodes = 'nodes',
    Reports = 'reports',
    Resources = 'resources',
    Organizations = 'organizations',
  }

  export enum Role {
    User = 'user',
    Manager = 'manager',
    Admin = 'admin',
  }

  export type User = {
    name: string;
    email: string;
    createdAt: string;
    termsAccepted: boolean;
    lastName: null | string;
    firstName: null | string;
    role?: Role;
  };

  export type SystemUser = {
    name: string;
    role: Role;
    createdAt: string;
    updatedAt: string;
  };

  type CloudStorageInstance = {
    name: string;
    ready: boolean;
    size: number;
  };

  type EnergySchedule = {
    name: string;
    pricePerKwh: string;
    periods: any[];
  };

  export type Cluster = {
    name: string;
    status: string;
    logoUrl: string;
    location: string;
    apps: {
      appsHostnameTemplates: string[];
    };
    createdAt: string;
    cloudProvider: {
      type: string;
      locationType: string;
      region: string;
      zones: string[];
      project: string;
      credentials: {
        privateKey: string;
      };
      nodePools: ClusterSettings.NodePool[];
      storage: {
        description: string;
        tier: string;
        instances: CloudStorageInstance[];
      };
    };
    storage: {
      url: string;
      volumes: (Shared.Cluster.StorageVolume & { size: number })[];
    };
    blobStorage: {
      url: string;
    };
    registry: {
      url: string;
    };
    orchestrator: {
      jobHostnameTemplate: string;
      isHttpIngressSecure: boolean;
      resourcePoolTypes: Shared.Cluster.ResourcePoolType[];
      jobFallbackHostname: string;
      jobScheduleTimeoutS: number;
      jobScheduleScaleUpTimeoutS: number;
      allowPrivilegedMode: boolean;
      allowJobPriority: boolean;
      resourcePresets?: Shared.Cluster.ResourcePreset[];
      prePullImages: string[];
    };
    monitoring: {
      url: string;
    };
    secrets: {
      url: string;
    };
    metrics: {
      url: string;
    };
    disks: {
      url: string;
      storageLimitPerUser: number;
      storageLimitPerUserGb: number;
    };
    buckets: {
      url: string;
      disableCreation: boolean;
    };
    ingress: {
      acmeEnvironment: string;
      defaultCorsOrigins: string[];
      additionalCorsOrigins: string[];
      corsOrigins: string[];
    };
    dns: {
      name: string;
    };
    energy: {
      schedules: EnergySchedule[];
      co2GramsEqPerKwh: number;
    };
    timezone: string;
    credentials: {
      neuro: {
        url: string;
        token: string;
      };
      neuroHelm: {
        url: string;
      };
      neuroRegistry: {
        url: string;
      };
      grafana: {
        username: string;
        password: string;
      };
      sentry: {
        clientKeyId: string;
        publicDsn: string;
        sampleRate: number;
      };
    };
  };

  export type ClusterOrganization = {
    orgName: string;
    maintenance?: boolean;
    defaultRole?: Shared.TenantRole;
    balance?: Partial<Shared.Balance>;
    quota?: ClusterSettings.Quota;
    defaultQuota?: ClusterSettings.Quota;
  };

  export type AllClusterOrganizationResponse = {
    name: string;
    balance: Shared.Balance;
    userDefaultCredits: null;
    notificationIntervals: {
      balanceProjectionSeconds: number[];
      balanceAmount: number[];
      balanceDepletionSeconds: number[];
    };
  };
}
